import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, Compass, FileText, Hammer, HandHeart, HouseLine, MagnifyingGlass, ShieldCheck, SpeakerHigh, SpeakerSlash, Star } from "@phosphor-icons/react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { siteSettings } from "@/content/site";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const HERO_VIDEO = "/media/oasi-hero.mp4";

const PROBLEMS = [
  "Non sai da dove iniziare.",
  "Hai paura che il prezzo cambi durante i lavori.",
  "Non vuoi perdere tempo tra mille fornitori.",
  "Vuoi un bagno bello, ma anche pratico.",
  "Vuoi sapere chi fa cosa e quando.",
  "Vuoi evitare lavori fatti male o da sistemare dopo.",
];

const METHOD = [
  { icon: HouseLine, t: "Ci racconti il tuo bagno", d: "Compili il modulo o ci scrivi su WhatsApp. Ti chiediamo com'è il bagno oggi, cosa vuoi cambiare, quanto è urgente l'intervento e dove si trova l'immobile." },
  { icon: MagnifyingGlass, t: "Facciamo il sopralluogo gratuito", d: "Verifichiamo misure, impianti, accessi, condizioni del bagno e necessità reali, evitando preventivi generici fatti a occhio." },
  { icon: Compass, t: "Ti guidiamo nelle scelte", d: "Ti aiutiamo a scegliere finiture, sanitari, mobile, doccia, rubinetteria e dettagli in modo ordinato." },
  { icon: FileText, t: "Prepariamo una proposta chiara", d: "Costruiamo una soluzione adatta al tuo bagno, con lavorazioni previste, materiali scelti e organizzazione dei tempi." },
  { icon: Hammer, t: "Organizziamo il cantiere", d: "Prima di iniziare, verifichiamo che tutto sia pronto: materiali, professionisti, tempi e fasi di lavoro." },
  { icon: HandHeart, t: "Realizziamo il tuo nuovo bagno", d: "Una volta iniziato il cantiere, lavoriamo con una sequenza organizzata per consegnarti il bagno nei tempi previsti." },
];

const PACKAGES = [
  {
    slug: "standard",
    name: "Standard",
    claim: "Essenziale, funzionale, ben fatto",
    desc: "Pensato per chi vuole rinnovare il bagno con una soluzione semplice, ordinata e concreta, senza rinunciare alla qualità dell'esecuzione.",
    cta: "Scopri se Standard è adatto al tuo bagno",
    features: ["Scelte guidate", "Materiali coordinati", "Garanzia standard", "2 tipologie di piastrelle selezionabili", "Computo metrico", "Organizzazione del lavoro", "Nessuna sorpresa"],
  },
  {
    slug: "superior",
    name: "Superior",
    badge: "Il più equilibrato",
    claim: "Più qualità, più comfort, più serenità",
    desc: "Pensato per chi vuole un bagno curato, pratico e più completo, con finiture migliori e soluzioni pensate per durare nel tempo.",
    cta: "Voglio un bagno più completo",
    features: ["Tutto ciò che è previsto nello Standard", "Super Garanzia", "Rubinetterie con risparmio energetico", "Aeratore anticalcare", "Ottone e finiture di pregio", "Docce con trattamento anticalcare", "3 scelte di piastrelle", "Maggiore cura nei dettagli"],
  },
  {
    slug: "exclusive",
    name: "Exclusive",
    claim: "Estetica, efficienza e sensazione di lusso",
    desc: "Pensato per chi vuole un bagno più ricercato, comodo da vivere e facile da mantenere nel tempo.",
    cta: "Voglio valutare una soluzione Exclusive",
    features: ["Tutto ciò che è previsto nel Superior", "Sanitari con trattamento antibatterico", "Trattamento impermeabilizzante per facilitare la pulizia", "Finiture più curate", "Maggiore attenzione estetica", "Esperienza più completa"],
  },
];

const WHY = [
  { t: "Scelte più semplici", d: "Ti aiutiamo a scegliere senza perderti tra mille opzioni. Ogni proposta è pensata per essere chiara, coerente e adatta al tuo bagno." },
  { t: "Metodo organizzato", d: "Prima di iniziare i lavori, controlliamo le informazioni importanti: misure, accessi, impianti, materiali e tempi." },
  { t: "Nessuna confusione", d: "Sai cosa è incluso, cosa viene scelto e quali passaggi servono per arrivare al bagno finito." },
  { t: "Risultato curato", d: "Non ci interessa solo finire il bagno. Ci interessa consegnarti un risultato ordinato, funzionale e piacevole da vivere." },
];

const GUIDE_POINTS = [
  "quali errori evitare",
  "cosa chiedere prima di iniziare",
  "quali informazioni servono per un preventivo serio",
  "perché il prezzo da solo non basta",
  "come capire se un lavoro è organizzato bene",
  "cosa può creare ritardi o costi extra",
];

const FAQ = [
  { q: "Il sopralluogo è gratuito?", a: "Sì, il sopralluogo è gratuito nelle zone servite. Serve a capire meglio il bagno, le esigenze e la fattibilità dell'intervento." },
  { q: "Il bagno viene davvero realizzato in 10 giorni?", a: "L'obiettivo è completare i lavori in circa 10 giorni di cantiere, partendo solo quando materiali e organizzazione sono pronti. I tempi possono variare in base alla complessità del bagno e alle condizioni rilevate." },
  { q: "Perché non mostrate i prezzi sul sito?", a: "Perché ogni bagno ha condizioni diverse: misure, impianti, accessi, materiali e livello di intervento cambiano molto da caso a caso. Preferiamo valutare prima il progetto e poi proporre una soluzione chiara." },
  { q: "Posso scegliere materiali e finiture?", a: "Sì. Ti guidiamo nella scelta di piastrelle, sanitari, mobile bagno, doccia, rubinetteria e dettagli, così puoi decidere con più semplicità e meno confusione." },
  { q: "Lavorate solo su ristrutturazioni complete?", a: "Oasi Bagni è pensata soprattutto per chi vuole rifare il bagno con un percorso organizzato e completo. In alcuni casi possiamo valutare anche interventi più mirati." },
  { q: "In quali zone lavorate?", a: "Operiamo principalmente tra Milano, provincia di Milano, zona ovest Milano, Corbetta, Magenta, Abbiategrasso, Vittuone, Sedriano, Arluno, Rho, Legnano, Parabiago, Novara e zone limitrofe." },
];

export default function Home() {
  const [videoFailed, setVideoFailed] = useState(false);
  const [videoMuted, setVideoMuted] = useState(true);
  const videoRef = useRef(null);

  const toggleVideoAudio = () => {
    const nextMuted = !videoMuted;
    setVideoMuted(nextMuted);
    if (videoRef.current) {
      videoRef.current.muted = nextMuted;
      if (!nextMuted) {
        videoRef.current.volume = 1;
        videoRef.current.play().catch(() => {});
      }
    }
  };

  return (
    <div className="bg-warm-white text-anthracite">
      <Navbar />

      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10 pt-12 md:pt-20 pb-16 md:pb-24 grid md:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="overline mb-5">Ristrutturazione bagno chiavi in mano</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">Il tuo nuovo bagno in 10 giorni, senza stress e imprevisti.</h1>
            <p className="mt-6 text-base md:text-lg text-[#5C5C59] leading-relaxed max-w-xl">Con Oasi Bagni hai un percorso guidato per ristrutturare il bagno: sopralluogo, scelte chiare, materiali coordinati e lavori organizzati dall'inizio alla fine.</p>
            <p className="mt-4 text-sm md:text-base text-[#5C5C59] leading-relaxed max-w-xl">Niente preventivi confusi, decisioni lasciate al caso o cantieri senza controllo. Ti accompagniamo passo dopo passo verso un bagno bello, funzionale e fatto bene.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/prenota-sopralluogo" className="btn-beige inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-sm font-medium" data-testid="hero-cta-primary">
                Richiedi sopralluogo gratuito <ArrowRight size={18} />
              </Link>
              <Link to="/guida-ristrutturazione-bagno" className="btn-beige-outline inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-sm font-medium" data-testid="hero-cta-secondary">
                Scarica la guida gratuita
              </Link>
            </div>
            <div className="mt-4 text-xs text-[#5C5C59]">Ti aiutiamo a capire cosa serve davvero prima di iniziare i lavori.</div>
            <div className="mt-10 grid grid-cols-2 gap-3 max-w-lg">
              {["Sopralluogo gratuito", "Metodo chiaro", "Materiali coordinati", "Bagno in 10 giorni", "Assistenza post-consegna"].map((t) => (
                <div key={t} className="flex items-center gap-2 text-sm text-[#5C5C59]">
                  <CheckCircle size={18} weight="fill" className="text-oasi-green flex-shrink-0" /> {t}
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="relative flex justify-center">
            {!videoFailed ? (
              <div className="relative w-full max-w-[390px]">
                <video ref={videoRef} className="w-full aspect-[9/16] max-h-[680px] object-contain rounded-3xl bg-sand-soft shadow-sm" autoPlay muted={videoMuted} loop playsInline preload="metadata" aria-label="Video Oasi Bagni" onError={() => setVideoFailed(true)}>
                  <source src={HERO_VIDEO} type="video/mp4" />
                </video>
                <button type="button" onClick={toggleVideoAudio} className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 text-xs font-medium text-anthracite shadow-sm backdrop-blur transition hover:bg-white" title={videoMuted ? "Attiva audio" : "Disattiva audio"} aria-label={videoMuted ? "Attiva audio del video" : "Disattiva audio del video"}>
                  {videoMuted ? <SpeakerSlash size={18} /> : <SpeakerHigh size={18} />}
                  Audio
                </button>
              </div>
            ) : (
              <div className="w-full max-w-[390px] aspect-[9/16] max-h-[680px] rounded-3xl bg-sand-soft border border-brand shadow-sm flex items-center justify-center p-8 text-center">
                <div>
                  <div className="overline mb-3">Metodo Oasi</div>
                  <div className="font-display text-3xl leading-tight">Il bagno nuovo, senza caos.</div>
                  <p className="mt-4 text-sm text-[#5C5C59] leading-relaxed">Un percorso guidato, ordinato e seguito dall'inizio alla fine.</p>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      <section className="bg-sand-soft section-pad">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <div className="text-center">
            <div className="overline mb-4">Il problema</div>
            <h2 className="font-display text-3xl md:text-5xl leading-tight">Rifare il bagno non dovrebbe diventare una fonte di stress.</h2>
            <p className="mt-6 text-base md:text-lg text-[#5C5C59] leading-relaxed max-w-3xl mx-auto">Quando si decide di ristrutturare il bagno, spesso iniziano dubbi, preventivi difficili da confrontare, scelte infinite, tempi poco chiari e paura di brutte sorprese.</p>
            <p className="mt-4 text-[#5C5C59] leading-relaxed max-w-3xl mx-auto">Il problema non è solo scegliere piastrelle, sanitari o rubinetteria. Il vero problema è avere un metodo.</p>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm text-[#5C5C59]">
            {PROBLEMS.map((p) => <div key={p} className="bg-white rounded-xl border border-brand py-3 px-4">{p}</div>)}
          </div>
          <p className="mt-10 text-center text-[#5C5C59] leading-relaxed">Per questo abbiamo creato Oasi Bagni: un modo più semplice, ordinato e guidato per rifare il bagno.</p>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-4xl mx-auto px-5 md:px-10 text-center">
          <div className="overline mb-4">La soluzione</div>
          <h2 className="font-display text-3xl md:text-5xl">Un metodo chiaro per trasformare il tuo bagno.</h2>
          <p className="mt-6 text-base md:text-lg text-[#5C5C59] leading-relaxed">Oasi Bagni nasce per togliere confusione dal processo di ristrutturazione. Ti guidiamo nella scelta delle soluzioni più adatte, organizziamo le fasi di lavoro e ti aiutiamo a visualizzare il risultato prima di iniziare.</p>
          <p className="mt-4 text-[#5C5C59] leading-relaxed">L'obiettivo non è venderti un bagno qualsiasi. L'obiettivo è consegnarti un bagno curato, funzionale e coerente con le tue esigenze.</p>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            {["Sopralluogo gratuito", "Scelte guidate", "Materiali coordinati", "Computo chiaro", "Cantiere organizzato", "Lavori con metodo", "Nessuna sorpresa inutile", "Un referente"].map((t) => (
              <div key={t} className="card-soft p-4 flex items-center gap-2">
                <CheckCircle size={16} weight="fill" className="text-oasi-green flex-shrink-0" /> {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-sand-soft section-pad">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="text-center mb-12">
            <div className="overline mb-4">Due strade, una soluzione</div>
            <h2 className="font-display text-3xl md:text-5xl">Da dove vuoi iniziare?</h2>
            <p className="mt-5 text-[#5C5C59] max-w-2xl mx-auto">Se sei pronto a parlare del tuo bagno, richiedi un sopralluogo gratuito. Se vuoi prima informarti, scarica la guida gratuita.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="card-soft p-8 md:p-10">
              <h3 className="font-display text-2xl md:text-3xl mb-3">Vuoi capire cosa si può fare nel tuo bagno?</h3>
              <p className="text-[#5C5C59] mb-6 leading-relaxed">Ogni bagno è diverso. Per questo non crediamo nei preventivi generici fatti senza vedere l'ambiente.</p>
              <Link to="/prenota-sopralluogo" className="btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm">Richiedi sopralluogo gratuito <ArrowRight size={16} /></Link>
              <p className="mt-4 text-xs text-[#5C5C59] italic">Il sopralluogo non ti obbliga ad acquistare. Serve a capire meglio il tuo progetto.</p>
            </div>
            <div className="card-soft p-8 md:p-10">
              <h3 className="font-display text-2xl md:text-3xl mb-3">Vuoi prima informarti?</h3>
              <p className="text-[#5C5C59] mb-6 leading-relaxed">Scarica la guida gratuita e scopri cosa valutare prima di rifare il bagno, quali errori evitare e come affrontare la ristrutturazione con più chiarezza.</p>
              <Link to="/guida-ristrutturazione-bagno" className="btn-outline inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm">Scarica la guida gratuita <ArrowRight size={16} /></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="text-center mb-14">
            <div className="overline mb-4">Come funziona</div>
            <h2 className="font-display text-3xl md:text-5xl">Come funziona Oasi Bagni.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {METHOD.map((m, i) => {
              const Icon = m.icon;
              return (
                <div key={i} className="card-soft p-6">
                  <div className="w-11 h-11 rounded-full bg-sand-soft flex items-center justify-center mb-4">
                    <Icon size={20} weight="light" className="text-oasi-green" />
                  </div>
                  <div className="overline text-[10px] mb-1">Step {i + 1}</div>
                  <div className="font-display text-xl mb-2">{m.t}</div>
                  <div className="text-sm text-[#5C5C59] leading-relaxed">{m.d}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-sand-soft section-pad">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="text-center mb-12">
            <div className="overline mb-4">Pacchetti</div>
            <h2 className="font-display text-3xl md:text-5xl">Tre livelli, un solo metodo.</h2>
            <p className="mt-5 text-[#5C5C59] max-w-2xl mx-auto">Ogni bagno ha esigenze diverse. Per questo abbiamo creato tre percorsi, pensati per aiutarti a scegliere con più chiarezza.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {PACKAGES.map((p) => (
              <div key={p.slug} className={`card-soft p-7 relative ${p.badge ? "ring-1 ring-[var(--oasi-green)] md:scale-[1.02]" : ""}`} data-testid={`pack-${p.slug}`}>
                {p.badge && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-oasi-green text-white text-[11px] tracking-[0.18em] uppercase px-4 py-1 rounded-full flex items-center gap-1"><Star size={12} weight="fill" /> {p.badge}</div>}
                <div className="font-display text-2xl">{p.name}</div>
                <div className="text-oasi-green text-sm mt-1">{p.claim}</div>
                <p className="text-sm text-[#5C5C59] mt-3 leading-relaxed">{p.desc}</p>
                <ul className="mt-5 space-y-2">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex gap-2 text-sm">
                      <CheckCircle size={16} weight="fill" className="text-oasi-green mt-0.5 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/prenota-sopralluogo" className={`mt-6 inline-flex items-center justify-center text-center gap-2 px-5 py-2.5 rounded-full text-sm w-full leading-snug ${p.badge ? "btn-primary" : "btn-outline"}`}>
                  {p.cta} <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-[#5C5C59] mt-6 max-w-2xl mx-auto italic">Non mostriamo prezzi sul sito: usiamo i pacchetti per far capire ordine e progressione, non per fare un configuratore generico.</p>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-5xl mx-auto px-5 md:px-10 text-center">
          <div className="overline mb-4">Il valore nel tempo</div>
          <h2 className="font-display text-3xl md:text-5xl">Non vendiamo solo bagni. Vendiamo anni di serenità.</h2>
          <p className="mt-6 text-[#5C5C59] text-base md:text-lg leading-relaxed max-w-3xl mx-auto">Un bagno non si giudica solo il giorno della consegna. Si giudica dopo mesi e anni: quando lo usi ogni giorno, quando lo pulisci, quando tutto funziona, quando non devi richiamare qualcuno per sistemare problemi evitabili.</p>
          <p className="mt-4 text-[#5C5C59] leading-relaxed max-w-3xl mx-auto">Un bagno fatto bene non deve solo essere bello in foto. Deve essere comodo, resistente e facile da vivere.</p>
        </div>
      </section>

      <section className="bg-sand-soft section-pad">
        <div className="max-w-5xl mx-auto px-5 md:px-10 grid md:grid-cols-2 gap-10 items-start">
          <div>
            <div className="overline mb-4">Tempi</div>
            <h2 className="font-display text-3xl md:text-5xl">10 giorni di lavoro, con una preparazione fatta prima.</h2>
          </div>
          <div>
            <p className="text-[#5C5C59] leading-relaxed">Quando diciamo "bagno in 10 giorni", parliamo dei giorni di lavoro effettivi in cantiere.</p>
            <p className="mt-4 text-[#5C5C59] leading-relaxed">Prima di iniziare, organizziamo materiali, fornitori, professionisti e fasi operative. Questo permette di ridurre ritardi, blocchi e improvvisazioni.</p>
            <p className="mt-5 text-xs text-[#5C5C59] italic">I tempi possono variare in base alle condizioni del bagno, alla complessità dell'intervento e alla disponibilità dei materiali scelti. Il nostro obiettivo è partire solo quando tutto è pronto.</p>
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="text-center mb-12">
            <div className="overline mb-4">Perché sceglierci</div>
            <h2 className="font-display text-3xl md:text-5xl">Perché scegliere Oasi Bagni.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {WHY.map((item) => (
              <div key={item.t} className="card-soft p-6">
                <ShieldCheck size={22} weight="light" className="text-oasi-green mb-4" />
                <div className="font-display text-xl mb-2">{item.t}</div>
                <p className="text-sm text-[#5C5C59] leading-relaxed">{item.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-sand-soft section-pad">
        <div className="max-w-5xl mx-auto px-5 md:px-10 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="overline mb-4">Guida gratuita</div>
            <h2 className="font-display text-3xl md:text-5xl mb-5">Scarica la guida gratuita prima di rifare il bagno.</h2>
            <p className="text-[#5C5C59] leading-relaxed mb-6">Una guida semplice per capire cosa controllare prima di iniziare una ristrutturazione bagno.</p>
            <ul className="space-y-2 mb-7">
              {GUIDE_POINTS.map((f) => (
                <li key={f} className="flex gap-2 text-sm">
                  <CheckCircle size={16} weight="fill" className="text-oasi-green mt-0.5 flex-shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <Link to="/guida-ristrutturazione-bagno" className="btn-outline inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm">Scarica la guida gratuita <ArrowRight size={16} /></Link>
            <p className="mt-4 text-xs text-[#5C5C59] italic">Ti invieremo la guida e potrai valutare con più consapevolezza il tuo progetto.</p>
          </div>
          <div className="flex justify-center">
            <div className="relative w-[280px] h-[360px] card-soft overflow-hidden rotate-[-3deg]">
              <div className="absolute inset-0 bg-sand flex items-center">
                <div className="w-[30%] bg-white h-full p-4 flex flex-col justify-between">
                  <div className="text-[9px] uppercase tracking-[0.2em] font-medium">Oasi Bagni</div>
                  <div className="text-[8px] text-[#5C5C59] leading-tight">La guida per partire bene senza stress</div>
                </div>
                <div className="flex-1 pr-5 pl-3">
                  <div className="text-[9px] uppercase tracking-[0.2em] text-white/80 mb-3">Guida gratuita</div>
                  <div className="font-display text-white text-3xl leading-[0.95] font-semibold">BAGNO<br/>SERENO<br/>SUBITO</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-3xl mx-auto px-5 md:px-10">
          <div className="overline mb-4 text-center">FAQ</div>
          <h2 className="font-display text-3xl md:text-5xl mb-10 text-center">Domande frequenti</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQ.map((f, i) => (
              <AccordionItem key={i} value={`i-${i}`} className="card-soft px-5 border-none">
                <AccordionTrigger className="text-left font-medium text-anthracite hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[#5C5C59] leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-10">
            <Link to="/faq" className="text-sm text-oasi-green underline">Vedi tutte le FAQ</Link>
          </div>
        </div>
      </section>

      <section className="bg-oasi-green text-white section-pad">
        <div className="max-w-4xl mx-auto px-5 md:px-10 text-center">
          <h2 className="font-display text-3xl md:text-5xl mb-5">Vuoi trasformare il tuo bagno senza stress?</h2>
          <p className="text-white/80 mb-8 text-base md:text-lg">Raccontaci com'è il tuo bagno oggi. Ti aiuteremo a capire quale soluzione può essere più adatta, con un percorso chiaro e guidato.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/prenota-sopralluogo" className="bg-white text-oasi-green hover:bg-white/90 px-7 py-4 rounded-full text-sm font-medium transition-all">Richiedi sopralluogo gratuito</Link>
            <Link to="/guida-ristrutturazione-bagno" className="border border-white/60 text-white hover:bg-white/10 px-7 py-4 rounded-full text-sm font-medium transition-all">Scarica la guida gratuita</Link>
          </div>
          <p className="mt-5 text-xs text-white/70">Il primo passo è semplice: capire cosa serve davvero al tuo bagno.</p>
        </div>
      </section>

      <Footer settings={siteSettings} />
    </div>
  );
}
